import { useFacilityContext } from '@aspendental/shared-components-web';
import { BinaryChoice, BinaryChoiceSchema, Form, MaskedPhoneNumberInput, MaskedPhoneNumberInputSchema, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import Grid from '@mui/material/Grid';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { type FieldValues } from 'react-hook-form';
import { REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { DataLayerStepNames } from '@src/constants';
import { useLanguagePreferenceTracking } from '@src/hooks/useLanguagePreferenceTracking';
import { useScrollToFirstError } from '@src/hooks/useScrollToFirstError';
import { EventAdditionalPatientInformationValueEventTypeEnum } from '@src/lib/__generated/user-events-api';
import { schedulerLeadEvent } from '@src/services/schedulerLeadEvent';
import { userEvent } from '@src/services/userEvent';
import { PatientInformationSchema, PatientInformationSchemaES, useAppStore, useAppointmentStore, useFormSectionStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import { fireDataLayerEvent } from '@src/utils/data-layer-utils';
import { formatPhoneNumber } from '@src/utils/format-phone-number';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';
const AdditionalPatientInformation = ({
  formId,
  shouldUseMottoConfig = false
}: {
  formId: string;
  shouldUseMottoConfig?: boolean;
}) => {
  const {
    email,
    setEmail,
    phonenumber,
    setPhonenumber,
    hasInsurance,
    setHasInsurance,
    firstName,
    lastName,
    dateOfBirth
  } = usePatientInformationFormStore(store => store);
  const completeActiveNode = useFormSectionStore(store => store.completeActiveNode);
  const {
    appointmentFacility,
    appointmentSlot
  } = useAppointmentStore(store => store);
  const {
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const webFacilityDetails = useFacilityContext();
  const {
    mottoFlow
  } = useAppStore(store => store);
  const {
    t
  } = useTranslation('additionalPatientInformation');
  const router = useRouter();
  const {
    locale
  } = router;
  // setting the session storage key to false for the patient information step to prevent multiple data layer events
  sessionStorageHelper.setKey(DataLayerStepNames.additionalPatientInfo, 'false');
  useEffect(() => {
    //this method is added to prevent multiple data layer events from being fired in mobile view
    //pass the step name as first parameter
    fireDataLayerEvent(DataLayerStepNames.additionalPatientInfo, {
      language_preference: locale,
      brand: mottoFlow ? 'mottoaligners' : 'aspendental',
      lead_score: appointmentSlot?.patientValue,
      lead_tier_national: appointmentSlot?.nationalPatientTier,
      lead_tier_office: appointmentSlot?.patientTier,
      step_name: DataLayerStepNames.additionalPatientInfo,
      reason_for_visit: reasonForVisit,
      reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
      ...(webFacilityDetails?.officeInfo?.code && {
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code
      }),
      ...(webFacilityDetails?.officeInfo?.name && {
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      }),
      ...(appointmentFacility?.facilityCode && {
        appointment_facility_id: appointmentFacility?.facilityCode
      }),
      ...(appointmentFacility?.title && {
        appointment_facility_name: appointmentFacility?.title
      })
    });
  }, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, appointmentFacility?.facilityCode, appointmentFacility?.title, mottoFlow, appointmentSlot]);
  useLanguagePreferenceTracking({
    stepName: DataLayerStepNames.additionalPatientInfo,
    webFacilityDetails,
    brand: mottoFlow ? 'mottoaligners' : 'aspendental'
  });
  const schema = SchemaBuilder([{
    name: 'email',
    validation: locale === 'en-US' ? PatientInformationSchema.email : PatientInformationSchemaES.email
  }, {
    name: 'phonenumber',
    validation: MaskedPhoneNumberInputSchema.options.default
  }, {
    name: 'hasInsurance',
    validation: BinaryChoiceSchema.options.default
  }]);
  const defaultValues = {
    email,
    phonenumber: formatPhoneNumber(phonenumber) || phonenumber,
    hasInsurance
  };
  const form = useHookForm({
    schema,
    defaultValues,
    shouldFocusError: false
  });
  useScrollToFirstError(form as any, formId);
  const handleSubmit = (fields: FieldValues) => {
    const {
      email,
      phonenumber,
      hasInsurance
    } = fields;
    setEmail(email);
    setPhonenumber(phonenumber ?? '');
    setHasInsurance(hasInsurance);
    void schedulerLeadEvent({
      firstName,
      lastName,
      email,
      dateOfBirth,
      reasonForVisit,
      shouldUseMottoConfig
    });
    if (typeof completeActiveNode === 'function') {
      completeActiveNode();
    }
    userEvent({
      event_type: EventAdditionalPatientInformationValueEventTypeEnum.AdditionalPatientInfo,
      event_data: {
        facility_code: appointmentFacility?.facilityCode ?? '',
        first_name: firstName,
        last_name: lastName,
        date_of_birth: new Intl.DateTimeFormat('en-US', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(dateOfBirth ?? undefined),
        email_address: email,
        mobile_phone: phonenumber,
        is_insured: hasInsurance
      }
    });
  };
  return <Form form={form as any} onSubmit={handleSubmit} formId={formId} data-sentry-element="Form" data-sentry-component="AdditionalPatientInformation" data-sentry-source-file="AdditionalPatientInformationForm.tsx">
			<Grid container spacing={2} data-sentry-element="Grid" data-sentry-source-file="AdditionalPatientInformationForm.tsx">
				<Grid item xs={12} sm={7} data-private data-sentry-element="Grid" data-sentry-source-file="AdditionalPatientInformationForm.tsx">
					<TextInput name="email" label={t('emailLabel')} enforceAlphaNumeric={false} placeholder="email@domain.com" data-sentry-element="TextInput" data-sentry-source-file="AdditionalPatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={7} data-private data-sentry-element="Grid" data-sentry-source-file="AdditionalPatientInformationForm.tsx">
					<MaskedPhoneNumberInput name="phonenumber" label={t('phoneNumLabel')} data-sentry-element="MaskedPhoneNumberInput" data-sentry-source-file="AdditionalPatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={7} data-sentry-element="Grid" data-sentry-source-file="AdditionalPatientInformationForm.tsx">
					<BinaryChoice groupLabel={t('hasInsurance')} name="hasInsurance" defaultSelection={defaultValues.hasInsurance} truthyLabel={t('yes')} falsyLabel={t('no')} variant="bodyLargeBook" helperMessage={t('selectionReq')} data-sentry-element="BinaryChoice" data-sentry-source-file="AdditionalPatientInformationForm.tsx" />
				</Grid>
			</Grid>
		</Form>;
};
export default AdditionalPatientInformation;